/**
 * shared/api/middleware — Auth-aware fetch hook points.
 *
 * Provides openapi-fetch middleware for:
 *   1. Token-attach: injects "Authorization: Bearer <token>" when a token is available.
 *   2. onUnauthorized: hook point called on 401 responses — stub now, Phase 3 wires refresh.
 *   3. Active-org header: injects "X-Org-ID: <uuid>" on every request when an active org
 *      is set in the org store. Reads from an injectable accessor (same pattern as the
 *      token-accessor) so the middleware does not import from entities directly.
 *   4. Request-ID: generates a client-side UUID per request, sends it as X-Request-ID,
 *      and registers it with the observability module for FE↔BE Sentry correlation.
 *      The backend mirrors this header in its structured logs (Phase 1 middleware).
 *
 * All hook points are injectable so feature slices can supply real implementations
 * without touching any call sites.
 *
 * NEVER hard-code tokens or org IDs here — always delegate to the accessor contracts.
 */

import type { Middleware } from 'openapi-fetch';
import { getAccessToken } from '../auth/token';

// ---------------------------------------------------------------------------
// Active-org ID accessor seam
// ---------------------------------------------------------------------------

/** Signature for a function that returns the current active org ID. */
export type ActiveOrgIdAccessor = () => string | null | undefined;

/** No-op stub — returns null (no active org) until the org store registers one. */
const noopOrgAccessor: ActiveOrgIdAccessor = () => null;

/** The currently registered active-org ID accessor. */
let _activeOrgIdAccessor: ActiveOrgIdAccessor = noopOrgAccessor;

/**
 * Register a real active-org ID accessor.
 * Called once by the org entity store (entities/organization/model/org-store.ts)
 * during module evaluation, mirroring how the token accessor is wired.
 */
export function setActiveOrgIdAccessor(accessor: ActiveOrgIdAccessor): void {
  _activeOrgIdAccessor = accessor;
}

/**
 * Get the current active org ID by delegating to the registered accessor.
 * Returns null when no accessor has been registered or no org is active.
 */
export function getActiveOrgIdFromMiddleware(): string | null | undefined {
  return _activeOrgIdAccessor();
}

/**
 * Reset to the no-op stub.
 * Used in tests to isolate accessor state between test cases.
 */
export function resetActiveOrgIdAccessor(): void {
  _activeOrgIdAccessor = noopOrgAccessor;
}

// ---------------------------------------------------------------------------
// onUnauthorized hook point
// ---------------------------------------------------------------------------

/** Signature for the 401 handler — Phase 3 will inject token refresh here. */
export type UnauthorizedHandler = (response: Response) => Promise<void> | void;

/** No-op stub — does nothing until Phase 3 injects a real handler. */
const noopUnauthorizedHandler: UnauthorizedHandler = () => undefined;

/** The currently registered 401 handler. */
let _unauthorizedHandler: UnauthorizedHandler = noopUnauthorizedHandler;

/**
 * Register a real 401 handler (e.g., token refresh + retry).
 * Called once by the auth slice during app init (Phase 3).
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  _unauthorizedHandler = handler;
}

/**
 * Reset to the no-op stub.
 * Used in tests to isolate handler state between test cases.
 */
export function resetUnauthorizedHandler(): void {
  _unauthorizedHandler = noopUnauthorizedHandler;
}

// ---------------------------------------------------------------------------
// Request-ID seam
//
// The request ID is a per-request UUID sent as `X-Request-ID` to the backend.
// The backend's Phase-1 RequestIDMiddleware reads this header and propagates it
// into structured logs. The observability module reads the most recent request
// ID from this seam and tags Sentry events with it so a frontend error can be
// correlated to backend log lines.
//
// The observability module is imported lazily (dynamic import inside the setter
// call) to avoid a hard dependency on @sentry/react in this file.
// ---------------------------------------------------------------------------

/**
 * Generate a simple UUID v4. Uses the Web Crypto API when available (all
 * modern browsers and Node 19+), falls back to a Math.random() approximation.
 */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// openapi-fetch middleware
// ---------------------------------------------------------------------------

/**
 * Auth middleware that:
 *   - Attaches the Bearer token (when available) to every outgoing request.
 *   - Attaches the X-Org-ID header (when an active org is set) to every request.
 *   - Generates and attaches X-Request-ID for FE↔BE log correlation.
 *   - Calls the registered onUnauthorized handler on 401 responses.
 *
 * No-ops gracefully when no token / handler / org is registered — unauthenticated
 * or org-less calls proceed without those headers and 401s pass through unmodified.
 */
export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    // Inject the active-org header for org-scoped requests.
    const orgId = getActiveOrgIdFromMiddleware();
    if (orgId) {
      request.headers.set('X-Org-ID', orgId);
    }

    // Generate and attach a per-request ID for FE↔BE log correlation.
    // The observability module tags Sentry events with this ID so that a
    // frontend error can be traced back to the matching backend log lines.
    const requestId = generateRequestId();
    request.headers.set('X-Request-ID', requestId);

    // Register with the observability seam (lazy import — no cost when disabled).
    import('../observability')
      .then(({ setCurrentRequestId }) => {
        setCurrentRequestId(requestId);
      })
      .catch(() => {
        // Never let observability wiring break API calls.
      });

    return request;
  },

  async onResponse({ response }) {
    if (response.status === 401) {
      await _unauthorizedHandler(response);
    }
    return response;
  },
};
